/**
 * Priority-based collapse for the quick-props bar's row of chips: given the measured width of
 * each item and the width of everything in the bar that never folds, decide which items still fit
 * and which get folded into the "+N" chip.
 */

/**
 * Comfort cap for the whole selection bar, in px. The bar is a quick-edit strip, not a second property
 * panel: past this width quick props fold into "+N" even when the canvas has room for more.
 */
export const SELECTION_BAR_MAX_WIDTH: number = 720;

export interface ICollapseInput {
  itemWidths: number[];
  /** Lower number = more important. Ties break by dropping the later (higher-index) item first. */
  priorities: number[];
  /**
   * Everything in the bar outside the quick props: the drag grip, the breadcrumb segment, the separator,
   * the action buttons, style tag, ⋯, the gaps between them, the bar's padding and border.
   */
  fixedWidth: number;
  /** Width of the "+N" chip, counted once something has actually been dropped. */
  moreWidth: number;
  /** The `gap` between neighbouring controls inside the quick-props region. */
  gapWidth: number;
  maxWidth: number;
}

/** The whole bar's width when `kept` items (plus the "+N" chip when something was dropped) are shown. */
export function collapsedBarWidth(input: ICollapseInput, kept: number[], droppedAny: boolean): number {
  const itemsSum: number = kept.reduce((sum: number, i: number): number => sum + input.itemWidths[i], 0);
  const children: number = kept.length + (droppedAny ? 1 : 0);
  const gaps: number = Math.max(0, children - 1) * input.gapWidth;
  const more: number = droppedAny ? input.moreWidth : 0;
  return itemsSum + gaps + more + input.fixedWidth;
}

/**
 * How much more room, in px, an unfold needs than a fold. The budget moves when the crumb merges into or
 * leaves the bar, or a style tag's value changes; without a band a budget hovering at an item's edge
 * would fold and unfold it on every change.
 */
export const UNFOLD_HYSTERESIS: number = 24;

/** Items in the order they stay: the reverse of the order they are dropped in. */
function keepOrder(input: ICollapseInput): number[] {
  return input.itemWidths
    .map((_: number, i: number): number => i)
    .sort((a: number, b: number): number => {
      if (input.priorities[a] !== input.priorities[b]) {
        return input.priorities[a] - input.priorities[b];
      }
      return a - b;
    });
}

const ascending = (indices: number[]): number[] => [...indices].sort((a: number, b: number): number => a - b);

/** Whether `kept` (plus the "+N" chip when it is not every item) fits `maxWidth`. */
function fits(input: ICollapseInput, kept: number[], maxWidth: number): boolean {
  return collapsedBarWidth(input, kept, kept.length < input.itemWidths.length) <= maxWidth;
}

/**
 * The items that stay visible under `maxWidth`, greedy by priority. Everything stays when everything fits
 * without a "+N" chip. Otherwise the chip's room is reserved and the items are walked in `keepOrder`,
 * keeping each one that still fits — so a wide early control that does not fit folds, while a narrower
 * later one that does fit stays (a GalleryView's 237px data-source chip no longer takes its 113px and 99px
 * neighbours with it). The result is not always a prefix of `keepOrder`.
 */
function keptSet(input: ICollapseInput, maxWidth: number): number[] {
  const order: number[] = keepOrder(input);
  if (fits(input, order, maxWidth)) {
    return ascending(order);
  }
  const kept: number[] = [];
  for (const index of order) {
    const candidate: number[] = [...kept, index];
    if (collapsedBarWidth(input, candidate, true) <= maxWidth) {
      kept.push(index);
    }
  }
  return ascending(kept);
}

/** Returns the original-order, ascending indices of items that stay visible. */
export function collapseProps(input: ICollapseInput): number[] {
  return keptSet(input, input.maxWidth);
}

/**
 * `collapseProps` with a hysteresis band: folds as soon as the bar would pass `maxWidth` (the cap is a
 * hard limit), but unfolds an item only when it would still be kept `UNFOLD_HYSTERESIS` px under the cap.
 * `currentKept` is the set visible now (ascending indices), or null when nothing is folded. The comparison
 * is by set, not count: the greedy fold can keep a later item while folding an earlier one.
 */
export function collapsePropsStable(input: ICollapseInput, currentKept: readonly number[] | null): number[] {
  const strict: number[] = keptSet(input, input.maxWidth);
  const current: ReadonlySet<number> = new Set<number>(
    currentKept ?? input.itemWidths.map((_: number, i: number): number => i)
  );
  if (strict.every((i: number): boolean => current.has(i))) {
    return strict;
  }
  const relaxed: ReadonlySet<number> = new Set<number>(keptSet(input, input.maxWidth - UNFOLD_HYSTERESIS));
  // An unfold must leave the band to spare for the bar as it will actually be, not per item.
  const unfolded: number[] = strict.filter((i: number): boolean => current.has(i) || relaxed.has(i));
  if (fits(input, unfolded, input.maxWidth - UNFOLD_HYSTERESIS)) {
    return unfolded;
  }
  const held: number[] = strict.filter((i: number): boolean => current.has(i));
  if (fits(input, held, input.maxWidth)) {
    return held;
  }
  // What is shown now no longer fits either: settle on what the band allows.
  return strict.filter((i: number): boolean => relaxed.has(i));
}

export interface IBarBudgetInput {
  /** The bar's rendered width — the breadcrumb segment included. */
  barWidth: number;
  /** The quick-props region's rendered width (shown controls and the "+N" chip). */
  regionWidth: number;
  /** The canvas safe area's width. */
  availableWidth: number;
}

export interface IBarBudget {
  fixedWidth: number;
  maxWidth: number;
}

/**
 * What the quick props must fit around, and into. A merged crumb segment is part of the bar and counts;
 * a crumb standing on its own above the element does not. Folding cannot flip the merge: the placement
 * engine decides the crumb against the bar at its cap (`crumbDecisionBarWidth`), not its current width.
 */
export function selectionBarBudget(input: IBarBudgetInput): IBarBudget {
  return {
    fixedWidth: input.barWidth - input.regionWidth,
    maxWidth: Math.min(SELECTION_BAR_MAX_WIDTH, input.availableWidth)
  };
}
